import { useEffect, useState, type FormEvent } from "react";
import { supabase } from "../../lib/supabase.js";
import { useAuth } from "../../lib/auth.js";
import { Button } from "../../lib/ui.js";
import { SectionCard, errorStyle } from "./common.js";

/**
 * The same choices the database accepts and the voice service understands (packages/shared/prompts/voice_style.ts,
 * migration 0033); a test in packages/shared keeps these lists in step with those.
 */
export const VOICE_CHOICES = {
  tone: {
    label: "Tone",
    options: [
      { key: "warm", label: "Warm", blurb: "Relaxed and welcoming, like a friendly local." },
      { key: "friendly", label: "Bright and upbeat", blurb: "Cheerful and energetic, a smile in the voice." },
      { key: "calm", label: "Calm and reassuring", blurb: "Steady and soothing, good for callers who are stressed." },
      { key: "professional", label: "Polished and professional", blurb: "Courteous and measured, a little more formal." },
    ],
  },
  pace: {
    label: "Pace",
    options: [
      { key: "relaxed", label: "Relaxed", blurb: "A little slower, with short pauses. Easier to follow." },
      { key: "natural", label: "Natural", blurb: "An ordinary conversational pace." },
      { key: "brisk", label: "Brisk", blurb: "Quick and efficient, for busy callers." },
    ],
  },
  style: {
    label: "Style",
    options: [
      { key: "concise", label: "To the point", blurb: "Short answers, no small talk unless the caller starts it." },
      { key: "balanced", label: "Balanced", blurb: "Professional and natural, neither clipped nor chatty." },
      { key: "conversational", label: "Chatty", blurb: "Happy to make friendly small talk before getting down to business." },
    ],
  },
} as const;

type Setting = keyof typeof VOICE_CHOICES;
export type VoiceChoice = Record<Setting, string>;
export const DEFAULT_VOICE_CHOICE: VoiceChoice = { tone: "warm", pace: "natural", style: "balanced" };

/** How the receptionist sounds on calls: tone, pace and style, each picked from a short list. */
export function VoiceStyleSection() {
  const { activeBusinessId } = useAuth();
  const [saved, setSaved] = useState<VoiceChoice>(DEFAULT_VOICE_CHOICE);
  const [choice, setChoice] = useState<VoiceChoice>(DEFAULT_VOICE_CHOICE);
  const [loaded, setLoaded] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!activeBusinessId) return;
    void supabase.rpc("rpc_portal_get_voice_style", { p_business_id: activeBusinessId }).then(({ data }) => {
      const row = (Array.isArray(data) ? data[0] : data) as VoiceChoice | null;
      if (row) {
        setSaved(row);
        setChoice(row);
      }
      setLoaded(true);
    });
  }, [activeBusinessId]);

  const changed = (Object.keys(VOICE_CHOICES) as Setting[]).some((k) => choice[k] !== saved[k]);

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!activeBusinessId) return;
    setError(null);
    setJustSaved(false);
    setBusy(true);
    const { error: err } = await supabase.rpc("rpc_portal_set_voice_style", {
      p_business_id: activeBusinessId,
      p_tone: choice.tone,
      p_pace: choice.pace,
      p_style: choice.style,
    });
    setBusy(false);
    if (err) {
      setError(err.message);
      return;
    }
    setSaved(choice);
    setJustSaved(true);
  }

  return (
    <SectionCard
      title="Voice and manner"
      description="How your receptionist sounds on the phone. Tone and pace are guidance the voice follows closely rather than an exact setting, so make a test call after changing them. Calls that start after you save use the new settings."
    >
      <form onSubmit={handleSave} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {(Object.keys(VOICE_CHOICES) as Setting[]).map((setting) => (
          <fieldset key={setting} style={{ border: "none", margin: 0, padding: 0 }}>
            <legend style={{ fontSize: 13, fontWeight: 600, color: "var(--rc-text-muted)", padding: 0, marginBottom: 6 }}>
              {VOICE_CHOICES[setting].label}
            </legend>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 8 }}>
              {VOICE_CHOICES[setting].options.map((o) => {
                const selected = choice[setting] === o.key;
                return (
                  <label
                    key={o.key}
                    style={{
                      display: "flex",
                      gap: 8,
                      alignItems: "flex-start",
                      padding: "9px 11px",
                      borderRadius: 8,
                      cursor: "pointer",
                      border: `1px solid ${selected ? "var(--rc-link)" : "var(--rc-border)"}`,
                      background: selected ? "var(--rc-surface-2, transparent)" : "transparent",
                    }}
                  >
                    <input
                      type="radio"
                      name={`voice-${setting}`}
                      value={o.key}
                      checked={selected}
                      disabled={!loaded}
                      onChange={() => {
                        setJustSaved(false);
                        setChoice((c) => ({ ...c, [setting]: o.key }));
                      }}
                      style={{ marginTop: 3 }}
                    />
                    <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--rc-text)" }}>{o.label}</span>
                      <span style={{ fontSize: 12, color: "var(--rc-text-muted)" }}>{o.blurb}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        ))}
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <Button type="submit" variant="primary" disabled={busy || !loaded || !changed}>
            {busy ? "Saving..." : "Save voice settings"}
          </Button>
          {justSaved && <span style={{ fontSize: 12.5, color: "var(--rc-success)" }}>Saved: new calls will use these settings.</span>}
        </div>
      </form>
      {error && <p style={errorStyle}>{error}</p>}
    </SectionCard>
  );
}
